import { Browser } from "@src-capacitor/@capacitor/browser"
import Base64url from "crypto-js/enc-base64url"
import Utf8 from "crypto-js/enc-utf8"
import { importJWK, JWK, JWTPayload, SignJWT } from "jose"
import {
  AuthenticationToken,
  ClientCredentials,
  DeviceRegistrationPayload,
  ValidAccessToken,
} from "src/auth/types"
import { authAPI } from "src/boot/axios"
import { LINKS } from "src/env/links"
import { SECRETS } from "src/env/secrets"
import router from "src/router"
import { useAuthStore } from "src/stores/authStore"
import { useCapacitorDeviceInfoStore } from "src/stores/capacitorDeviceInfoStore"
import { Log } from "src/util/classes/log"
import { NamedPath } from "src/util/enums/namedPaths"
import { AuthURL } from "src/util/enums/urls"
import { isBrowserInstalled } from "src/util/functions/misc"

/**
 * Generates a signed JSON Web Token (JWT) based on the software and devices' data.
 */
export async function generateJWT(): Promise<string> {
  // Issuer is validated on the server side. It needs to be this.
  const issuer = "SafeEx ApS"

  // HMAC with SHA-256
  const algorithm = "HS256"

  // Required for the server to be able to read the tokens.
  // Encode the secret to base64, make sure it is URL safe and remove any extra padding.
  const encodedSecret = Utf8.parse(SECRETS.DYNAMIC_APP_REG_JWT_SECRET).toString(Base64url)

  // "Kty" details the type of JWK. "Oct" stands for "Octet sequence" or a sequence of bytes.
  // Oct is used to represent symmetric keys. Symmetric means two parties share the secret key.
  const jwkObject: JWK = { kty: "oct", k: encodedSecret }
  const JWK = await importJWK(jwkObject, algorithm)

  const customPayload: JWTPayload = {}

  customPayload.platform = process.env.PLATFORM
  customPayload.version = process.env.BUILD_VERSION

  if (process.env.MODE === "capacitor") {
    const capacitorDeviceInfoStore = useCapacitorDeviceInfoStore()
    if (capacitorDeviceInfoStore.deviceId) {
      customPayload.deviceId = capacitorDeviceInfoStore.deviceId.identifier
    }
  }

  return await new SignJWT(customPayload)
    .setProtectedHeader({ typ: "JWT", alg: algorithm })
    .setIssuer(issuer)
    .sign(JWK)
}

/**
 * Registers the device on Springboard with data generated from {@link generateJWT}.
 *
 * If this is a development environment - it adds a redirect URI.
 * This sets the allowed redirects for {@link requestAuthorizationCode}.
 * The URI is the current URL string.
 *
 * @example Request
 * POST /sso/oauth/app/
 * Payload
 *  software_statement: "eyJ0eXAiOiJ..." // JWT string
 *  // Only for dev - the source of origin
 *  redirect_uris?: [localhost:8081] // Array with URLs. Accepts both HTTP and HTTPS, as well as ports
 */
export async function registerDevice(): Promise<ClientCredentials> {
  const url = AuthURL.AUTH_REGISTER_DEVICE
  const payload: Partial<DeviceRegistrationPayload> = {}
  const jwt = await generateJWT()

  // TODO: Set the production URL to the correct one
  // Manually setting the capacitor redirect uri as the backend needs to remain hardcoded to app.safeex.dk
  // for the old app the work. They are eventually going to change it, but we might as well always provide it.
  if (process.env.MODE === "capacitor") {
    payload.redirect_uris = [LINKS.SPA_TECHNICIAN_URL]
  } else {
    payload.redirect_uris = process.env.DEV ? [window.location.origin] : [getProdRedirectURI()]
  }

  payload.software_statement = jwt

  const formattedToken = "Bearer " + SECRETS.DYNAMIC_APP_REG_INITIAL_TOKEN

  // Axios doesn't really like the web API headers, so we just manually make a valid object
  const headers = { Authorization: formattedToken, "Content-Type": "application/json" }

  const { data } = await authAPI.post(url, payload, { headers })

  return data
}

function getProdRedirectURI() {
  return process.env.IS_MANAGER_MODE ? LINKS.SPA_MANAGER_URL : LINKS.SPA_TECHNICIAN_URL
}

/**
 * Generates unique URL based on provided client ID and code challenge.
 * Then said URL is opened in an internal browser if on Capacitor or else in the same window.
 *
 * If this is a development environment - it adds a redirect URI,
 * which points to where the redirect after login should happen.
 * The URI is the current URL string.
 *
 * @example Request
 * GET /sso/oauth/authorize
 * Query:
 *  client_id: "LYFjlMlDEWzx5..." // 40 chars
 *  response_type: "code" // Hardcoded
 *  code_challenge: "NdlWlNPxY..."
 *  code_challenge_method: "S256" // Hardcoded
 *
 * @param codeChallenge
 * @param clientId
 */
export async function requestAuthorizationCode(codeChallenge: string, clientId: string) {
  const endpoint = AuthURL.AUTH_REQUEST_AUTHORIZATION_CODE

  const searchParams = new URLSearchParams()
  searchParams.append("client_id", clientId)
  searchParams.append("response_type", "code")
  searchParams.append("code_challenge", codeChallenge)
  searchParams.append("code_challenge_method", "S256")

  // We handle the redirection for android with deep links
  if (process.env.DEV && process.env.MODE !== "capacitor") {
    searchParams.append("redirect_uri", window.location.origin)
  }

  const authUri = authAPI.getUri()

  const url = `${authUri}${endpoint}?${searchParams.toString()}`

  if (process.env.MODE === "capacitor") {
    // eslint-disable-next-line unicorn/prefer-ternary
    if (await isBrowserInstalled()) {
      // Browser check can be false positive. No point try cathing Browser.open as it directly crashes the app.
      await Browser.open({ url })
    } else {
      // There is no browser with which to continue the authentication flow.
      // Redirect to errounous page.
      await router.push({ name: NamedPath.NO_BROWSER })
    }
  } else {
    // Vue router is for internal links and quasars "openUrl" only opens in a new window
    window.location.replace(url)
  }
}

/**
 * Makes a request asking for an access and refresh tokens.
 * It requires data from all the previous authentication steps.
 *
 * @example Request
 * POST /sso/oauth/token/
 * Payload
 *  code: "lWJVppBLcms..." // Authorization Code
 *  client_id: "LYFjlMlDEWzx5..." // 40 chars
 *  grant_type: "authorization_code" // Hardcoded
 *  code_verifier: "AMNyISeCR6..." // 43 chars
 *
 * @param authorizationCode
 * @param clientId
 * @param codeVerifier
 */
export async function requestAccessToken(
  authorizationCode: string,
  clientId: string,
  codeVerifier: string
): Promise<AuthenticationToken> {
  const endpoint = AuthURL.AUTH_REQUEST_ACCESS_TOKEN

  const payload = new FormData()
  payload.append("code", authorizationCode)
  payload.append("client_id", clientId)
  payload.append("grant_type", "authorization_code")
  payload.append("code_verifier", codeVerifier)

  const { data } = await authAPI.post(endpoint, payload)

  return data
}

/**
 * Makes a request for verifying the access token.
 *
 * @example Request
 * GET /sso/verify/
 * Response
 *  id: "3bbadf3f-8d8b-4425...",
 *  email: "user@safeex.com",
 *  is_administrator: true,
 *
 * @param token
 */
export async function verifyAccessToken(token: string): Promise<ValidAccessToken> {
  const endpoint = AuthURL.AUTH_VERIFY_ACCESS_TOKEN

  const { data } = await authAPI.get(endpoint, {
    params: { access_token: token },
  })

  return data
}

/**
 * Makes a request for refreshing the access token.
 *
 * @example Request
 * POST /sso/oauth/token/
 * Payload
 *  client_id: "LYFjlMlDEWzx5..." // 40 chars
 *  grant_type: "refresh_token" // Hardcoded
 *  refresh_token: "TMf3zZcSgQcYuZIvz..."
 *
 * @param clientId
 * @param refreshToken
 */
export async function refreshAccessToken(
  clientId: string,
  refreshToken: string
): Promise<AuthenticationToken> {
  const endpoint = AuthURL.AUTH_REQUEST_ACCESS_TOKEN

  const payload = new FormData()
  payload.append("client_id", clientId)
  payload.append("grant_type", "refresh_token")
  payload.append("refresh_token", refreshToken)

  const { data } = await authAPI.post(endpoint, payload)

  return data
}

/**
 * Makes a request for revoking a token
 *
 * @example Request
 * POST /sso/oauth/revoke_token/
 * Payload
 *  client_id: "LYFjlMlDEWzx5..." // 40 chars
 *  access_token: "TMf3zZcSgQcYuZIvz..."
 *
 * @param clientId
 * @param token
 */
export async function revokeToken(clientId: string, token: string) {
  const endpoint = AuthURL.AUTH_REVOKE_TOKEN

  const payload = new FormData()
  payload.append("client_id", clientId)
  payload.append("token", token)

  await authAPI.post(endpoint, payload)
}

export async function loginSequence() {
  const authStore = useAuthStore()
  if (!authStore.canRequestAccessToken) {
    if (!authStore.clientCredentials) {
      // Register device if we don't have any data that it was already registered
      try {
        await authStore.registerDevice()
      } catch (deviceRegistrationError) {
        Log.error({ message: "Failed registering device.", error: deviceRegistrationError })
      }
    }

    // Try asking for the authorization code
    try {
      await authStore.requestAuthorizationCode()
    } catch (requestAuthCodeError) {
      Log.error({ message: "Failed requesting authorization code.", error: requestAuthCodeError })
    }
  } else {
    try {
      await authStore.getAccessToken()
      await router.push({ name: NamedPath.SELECT_WORKSPACE })
    } catch (error) {
      Log.error({ message: "Failed getting access token.", error })
    }
  }
}
